import {
  Component,
  OnInit,
  Inject,
  PLATFORM_ID,
  AfterViewChecked,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GlobalService } from '@core/services/global/global.service';
import { Subject, takeUntil } from 'rxjs';
import { GroupMember } from '@interface/Dealer';
import { InputSheetService } from '@core/services/InputSheet/input-sheet.service';
import {
  IgetInputSheetRequest,
  IPostInputSheetRequest,
} from '@interface/InputSheet';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { CustomDateSelectComponent } from '@shared/components/custom-date-select/custom-date-select.component';
import { ErrorPageComponent } from '@home/src/app/shared/components/app-error-page/app-error-page.component';
import { BaseErrorComponent } from '../baseerror/baseerror.component';

@Component({
  selector: 'app-input-sheets',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    CustomDateSelectComponent,
    ErrorPageComponent,
  ],
  templateUrl: './input-sheets.component.html',
  styleUrl: './input-sheets.component.css',
  providers: [],
})
export class InputSheetsComponent
  extends BaseErrorComponent
  implements OnInit, AfterViewChecked
{
  private readonly unsubscribe$ = new Subject<void>();
  minDate = new Date(new Date().getFullYear() - 1, new Date().getMonth() - 1);
  maxDate = new Date(new Date().getFullYear(), new Date().getMonth() - 1); // change -2 to -1 once the 2026 input sheets are available
  selectedDate: Date = this.maxDate;
  selectedDealer: GroupMember | null = null;
  errorMessage: string = '';
  successMessage: string = '';
  sheetDataTemplate: any = [];
  localSheetData: { [key: string]: string | number } = {};
  OrignalsheetData: { [key: string]: string | number } = {};
  updatedData: { [key: string]: string | number | null } = {};
  updatedCounter: boolean = false;
  isFinal: boolean = false;

  formulaDependencies: Record<string, string[]> = {};

  constructor(
    private globalService: GlobalService,
    private inputSheetService: InputSheetService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    super();
  }

  ngOnInit() {
    this.globalService.selectedDealer$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data) => {
        this.selectedDealer = data;
        this.updateInputSheetData();
      });
  }

  ngAfterViewChecked(): void {
    this.addRowNumbers();
  }

  addRowNumbers() {
    if (isPlatformBrowser(this.platformId)) {
      const elements = document?.getElementsByClassName(
        'counter-cell',
      ) as HTMLCollectionOf<HTMLInputElement>;
      if (!elements || elements.length === 0 || this.updatedCounter) return;
      for (let i = 0; i < elements.length; i++) {
        elements[i].innerHTML = `${i + 1}`;
      }
      this.updatedCounter = true;
    }
  }

  onDateSelected(date: Date): void {
    this.selectedDate = date;
    this.updateInputSheetData();
  }

  async updateInputSheetData() {
    this.resetErrorPage();
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.selectedDealer) return;
    this.globalService.updateShowOverlaySpinner(true);
    const request: IgetInputSheetRequest = {
      groupMemberCode: this.selectedDealer.groupMemberCode,
      stmt_year: this.selectedDate.getFullYear(),
      stmt_month: this.selectedDate.getMonth() + 1,
      template: true,
    };
    try {
      const response = await this.inputSheetService.getInputSheetData(request);
      if (response.statusCode !== 200) {
        this.globalService.showToast(
          response.data.error || response.data.message || 'Error fetching data',
          'error',
        );
        this.errorMessage =
          response.data.error || response.data.message || 'Error fetching data';
        this.sheetDataTemplate = [];
        this.localSheetData = {};
        this.OrignalsheetData = {};
        this.updatedCounter = false;
        this.setErrorPage(response.statusCode);
        return;
      }
      this.sheetDataTemplate = response.data.template;

      // Handle empty template (object OR array)  Temporary fix until backend is fixed to return consistent empty array for no data
      const isTemplateEmpty =
        !this.sheetDataTemplate ||
        (Array.isArray(this.sheetDataTemplate) &&
          this.sheetDataTemplate.length === 0) ||
        (!Array.isArray(this.sheetDataTemplate) &&
          typeof this.sheetDataTemplate === 'object' &&
          Object.keys(this.sheetDataTemplate).length === 0);

      if (isTemplateEmpty) {
        this.successMessage =
          response.data.message || 'Input sheet will soon be available.';
        this.sheetDataTemplate = [];
        this.localSheetData = {};
        this.OrignalsheetData = {};
        this.updatedCounter = false;
        return;
      }
      this.localSheetData = JSON.parse(JSON.stringify(response.data.data));
      this.OrignalsheetData = JSON.parse(JSON.stringify(response.data.data));
      this.isFinal = response.data.Finalized || false;
      this.buildFormulaDependencyMap();
    } catch (err: any) {
      console.log({ err });
      this.setErrorPage(err.statusCode || err.status);
      this.errorMessage =
        err?.errorMessage || err.message || 'Error fetching data';
    } finally {
      this.globalService.updateShowOverlaySpinner(false);
      this.addRowNumbers();
    }
  }

  buildFormulaDependencyMap() {
    this.formulaDependencies = {};

    for (const section of this.sheetDataTemplate) {
      for (const table of section.tables) {
        for (const rowGroup of table.tbody) {
          for (const row of rowGroup) {
            for (const cell of row) {
              if (cell?.formula && cell.id) {
                const ids = cell.formula.match(/\d+/g) || [];
                this.formulaDependencies[cell.id] = ids;
              }
            }
          }
        }
      }
    }
  }

  async onSubmit() {
    this.errorMessage = '';
    this.successMessage = '';
    if (Object.keys(this.updatedData).length === 0) {
      this.errorMessage = 'No data to update';
      this.globalService.showToast(this.errorMessage, 'error');
      return;
    }
    this.globalService.updateShowOverlaySpinner(true);
    if (!this.selectedDealer) return;
    const request: IPostInputSheetRequest = {
      groupMemberCode: this.selectedDealer?.groupMemberCode,
      stmt_year: this.selectedDate.getFullYear(),
      stmt_month: this.selectedDate.getMonth() + 1,
      data: this.updatedData,
    };
    try {
      const response: any =
        await this.inputSheetService.postInputSheetData(request);
      if (!response?.success) {
        this.globalService.showToast(
          response.message || response.error || 'Error updating data',
          'error',
        );
        this.errorMessage =
          response?.message || response.error || 'Error updating data';
        return;
      }
      this.globalService.showToast(
        response.message || 'Data updated successfully',
        'success',
      );
      this.successMessage = response?.message || 'Data updated successfully';
      this.localSheetData = JSON.parse(JSON.stringify(response?.data)) || {};
      this.OrignalsheetData = JSON.parse(JSON.stringify(response?.data)) || {};
      this.updatedData = {};
      this.updatedCounter = false;
    } catch (err) {
      console.log({ err });
    } finally {
      this.globalService.updateShowOverlaySpinner(false);
      this.addRowNumbers();
    }
  }

  onClear() {
    this.localSheetData = JSON.parse(JSON.stringify(this.OrignalsheetData));
    this.updatedData = {};
  }

  onPrint() {
    if (isPlatformBrowser(this.platformId)) {
      const printElement = document.getElementById('print');
      const clonedElement = printElement
        ? this.cloneWithInputValues(printElement)
        : null;
      const printContents = clonedElement?.innerHTML;

      const popupWin = window.open('', '_blank', 'width=800,height=600');

      if (popupWin && printContents) {
        popupWin.document.open();

        const styles = Array.from(
          document.querySelectorAll('link[rel="stylesheet"], style'),
        )
          .map((node) => node.outerHTML)
          .join('');

        popupWin.document.write(`
          <html>
            <head>
              <title>Print</title>
              ${styles}
            </head>
            <body onload="window.print(); window.close();">
              ${printContents}
            </body>
          </html>
        `);

        popupWin.document.close();
      }
    }
  }

  onUpdateData(key: string, event: number | null): void {
    this.updatedData[key] = event;
    if (event !== null) {
      this.updatedData[key] = event.toFixed(4).toString();
    }
    this.recalculateDependentFormulas(key);
  }

  recalculateDependentFormulas(changedId: string) {
    // Loop all formula cells and check dependencies
    Object.keys(this.formulaDependencies).forEach((formulaId) => {
      const deps = this.formulaDependencies[formulaId];

      if (deps.includes(changedId)) {
        // This formula depends on the changed input → recalc it
        const cellValue = this.recalculateFormula(formulaId);
        this.localSheetData[formulaId] = cellValue;
        this.updatedData[formulaId] = cellValue.toFixed(4);
      }
    });
  }

  recalculateFormula(cellId: string): number {
    const formulaCell = this.findCellById(cellId);
    if (!formulaCell) return 0;

    return this.calculateTotalValue(formulaCell);
  }

  findCellById(id: string) {
    for (const section of this.sheetDataTemplate) {
      for (const table of section.tables) {
        for (const rowGroup of table.tbody) {
          for (const row of rowGroup) {
            for (const cell of row) {
              if (cell.id === id) return cell;
            }
          }
        }
      }
    }
    return null;
  }

  calculateTotalValue(cell: any) {
    if (!cell.formula) return 0;

    const processedExpr = cell.formula.replace(/\d+/g, (id: any) => {
      const inputEl = document.getElementById(id) as HTMLInputElement;
      let value = inputEl ? inputEl.value || '0' : '0';
      if (Number(value) < 0) {
        value = `(${value})`;
      }
      return value;
    });
    const value = this.safeCalc(processedExpr);
    this.updatedData[cell.id] = value.toFixed(4);
    return value;
  }

  safeCalc(expression: string): number {
    try {
      // Only allow digits, parentheses and math operators
      if (!/^[0-9+\-*/().\s]+$/.test(expression)) {
        throw new Error('Invalid characters in expression');
      }
      return new Function(`return (${expression})`)();
    } catch {
      return 0;
    }
  }

  cloneWithInputValues(element: HTMLElement): HTMLElement {
    const clonedElement = element.cloneNode(true) as HTMLElement;

    const originalInputs = element.querySelectorAll('input, textarea, select');
    const clonedInputs = clonedElement.querySelectorAll(
      'input, textarea, select',
    );

    originalInputs.forEach((input, index) => {
      const clonedInput = clonedInputs[index];
      if (!clonedInput) return;

      if (input instanceof HTMLInputElement) {
        clonedInput.setAttribute('value', input.value);
      } else if (input instanceof HTMLTextAreaElement) {
        clonedInput.textContent = input.value;
      } else if (input instanceof HTMLSelectElement) {
        Array.from(clonedInput.children).forEach((option, i) => {
          if (
            option instanceof HTMLOptionElement &&
            input.options[i].selected
          ) {
            option.setAttribute('selected', 'true');
          } else {
            option.removeAttribute('selected');
          }
        });
      }
    });

    return clonedElement;
  }

  readKey(cellId: number | string, event: KeyboardEvent) {
    const { key, shiftKey } = event;

    if (
      key === 'Tab' ||
      key === 'Enter' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown'
    ) {
      const moveUp =
        key === 'ArrowUp' || (shiftKey && (key === 'Tab' || key === 'Enter'));

      this.moveUpAndDown(cellId, moveUp);
      event.preventDefault();
      return;
    }

    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      const { previousCellId, nextCellId } = this.findRowNavigation(cellId);
      const targetId = key === 'ArrowLeft' ? previousCellId : nextCellId;

      document.getElementById(String(targetId))?.focus();
      event.preventDefault();
    }
  }

  moveUpAndDown(cellId: number | string, shiftKey: boolean | null) {
    const value = shiftKey ? -1 : 1;
    let nextId = Number(cellId) + value;
    if (nextId < 1) return;
    const inputId = Object.keys(this.OrignalsheetData);
    const lastInputId = shiftKey
      ? 1
      : Math.max(...inputId.map((id) => Number(id)));
    while (
      (!shiftKey && lastInputId >= nextId) ||
      (shiftKey && nextId >= lastInputId)
    ) {
      const nextInput = document.getElementById(
        nextId.toString(),
      ) as HTMLInputElement;
      if (nextInput && !nextInput.disabled) {
        nextInput.focus();
        break;
      } else {
        nextId += value;
      }
    }
  }

  findRowNavigation(targetCellId: string | number) {
    const targetId = String(targetCellId);

    for (const section of this.sheetDataTemplate) {
      for (const table of section.tables ?? []) {
        for (const rowGroup of table.tbody ?? []) {
          for (const row of rowGroup) {
            const inputCells = row
              .filter(
                (cell: any) => cell.content === 'input' && cell.id !== null,
              )
              .map((cell: any) => String(cell.id));

            const index = inputCells.indexOf(targetId);

            if (index !== -1) {
              const current = inputCells[index];
              const previous = index > 0 ? inputCells[index - 1] : current;
              const next =
                index < inputCells.length - 1 ? inputCells[index + 1] : current;

              return {
                currentCellId: current,
                previousCellId: previous,
                nextCellId: next,
              };
            }
          }
        }
      }
    }
    return {
      currentCellId: targetCellId,
      previousCellId: targetCellId,
      nextCellId: targetCellId,
    };
  }

  async getInstructions() {
    try {
      await this.inputSheetService.getInstructionsDetails();
    } catch (e: any) {
      if (e.message === 'NO_DATA') {
        this.globalService.showToast(
          'Data not available. Please try again later.',
          'error',
        );
      } else {
        this.globalService.showToast(
          'Data not available. Please try again later.',
          'error',
        );
      }
    }
  }
}
